import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Table, Alert, Container } from "react-bootstrap";
import { collection, getDocs, query, where, getDoc, doc } from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";

const Invoice = () => {
  const [user, setUser] = useState(null); // Logged-in user
  const [userRole, setUserRole] = useState(null); // Role of the user
  const [permissions, setPermissions] = useState(null); // User permissions
  const [accessDenied, setAccessDenied] = useState(false); // Access control
  const [invoices, setInvoices] = useState([]); // Store user-specific invoices
  const [selectedInvoice, setSelectedInvoice] = useState(null); // Currently viewed invoice
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(""); // Error state

  // Fetch role and permissions
  const fetchUserRoleAndPermissions = async (uid) => {
    try {
      const userDocRef = doc(db, "Users", uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const role = userDoc.data().role;
        setUserRole(role);

        if (role === "admin") {
          setPermissions({ canViewInvoice: true }); // Admins always have access
          setAccessDenied(false);
          return;
        }

        const roleDocRef = doc(db, "Roles", role);
        const roleDoc = await getDoc(roleDocRef);

        if (roleDoc.exists()) {
          const rolePermissions = roleDoc.data().permissions;
          setPermissions(rolePermissions);

          if (!rolePermissions.canViewInvoice) {
            setAccessDenied(true);
          } else {
            setAccessDenied(false);
          }
        } else {
          console.error("Role document not found.");
          setAccessDenied(true);
        }
      } else {
        console.error("User document not found.");
        setAccessDenied(true);
      }
    } catch (error) {
      console.error("Error fetching role and permissions:", error);
      setAccessDenied(true);
    }
  };

  // Fetch invoices for the logged-in user
  const fetchUserInvoices = async (userEmail) => {
    try {
      const invoicesRef = collection(db, "Invoices");
      const userInvoicesQuery = query(invoicesRef, where("user.email", "==", userEmail));
      const invoicesSnapshot = await getDocs(userInvoicesQuery);

      const userInvoices = invoicesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setInvoices(userInvoices);

      // Automatically select the first invoice if available
      if (userInvoices.length > 0) {
        setSelectedInvoice(userInvoices[0]);
      }

      setLoading(false);
    } catch (err) {
      console.error("Error fetching user invoices:", err);
      setError("Failed to load invoices.");
      setLoading(false);
    }
  };

  // Monitor authentication state and fetch role/permissions
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setLoading(true);
        await fetchUserRoleAndPermissions(currentUser.uid);
        await fetchUserInvoices(currentUser.email);
      } else {
        setUser(null);
        setUserRole(null);
        setPermissions(null);
        setAccessDenied(true);
        setInvoices([]);
        setSelectedInvoice(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Header />
        <h5>Loading Invoices...</h5>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="text-center mt-5">
        <Header />
        <Alert variant="danger">Access Denied: You do not have permission to view invoices.</Alert>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center mt-5">
        <Header />
        <Alert variant="danger">{error}</Alert>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center mt-5">
        <Header />
        <Alert variant="info">Please log in to view your invoices.</Alert>
      </div>
    );
  }

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4 p-xxl-5">
        <h4 className="main-title mb-4">Your Invoices</h4>
        <Row>
          <Col md={4}>
            <Card>
              <Card.Body>
                <h5 className="mb-3">Select an Invoice</h5>
                {invoices.length > 0 ? (
                  <ul className="list-group">
                    {invoices.map((invoice) => (
                      <li
                        key={invoice.id}
                        className={`list-group-item ${
                          selectedInvoice?.id === invoice.id ? "active" : ""
                        }`}
                        onClick={() => setSelectedInvoice(invoice)}
                        style={{ cursor: "pointer" }}
                      >
                        Invoice #{invoice.id} - Total: ${invoice.totalCost.toFixed(2)}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No invoices found.</p>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={8}>
            {selectedInvoice ? (
              <Card>
                <Card.Body>
                  <div className="clearfix">
                    <div className="float-start mb-3">
                      <h3 className="m-0">Your Company Name</h3>
                    </div>
                    <div className="float-end">
                      <h4 className="m-0 d-print-none">Invoice</h4>
                    </div>
                  </div>
                  <Row className="mt-4">
                    <Col sm={6}>
                      <h6 className="fs-14">Invoice Details</h6>
                      <p>
                        <b>Order Date:</b>{" "}
                        {new Date(selectedInvoice.createdAt.seconds * 1000).toLocaleDateString()}
                      </p>
                      <p>
                        <b>Order ID:</b> {selectedInvoice.id}
                      </p>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <div className="table-responsive">
                        <Table className="table-centered table-hover table-borderless mb-0 mt-3">
                          <thead className="border-top border-bottom bg-light-subtle border-light">
                            <tr>
                              <th>#</th>
                              <th>Item</th>
                              <th>Description</th>
                              <th>Quantity</th>
                              <th>Unit Cost</th>
                              <th className="text-end">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedInvoice.items.map((item, index) => (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td>{item.name}</td>
                                <td>{item.description}</td>
                                <td>{item.quantity}</td>
                                <td>${Number(item.unitCost).toFixed(2)}</td>
                                <td className="text-end">
                                  ${(Number(item.unitCost) * Number(item.quantity)).toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col sm={6}>
                      <div className="pt-3">
                        <h6 className="text-muted fs-14">Notes:</h6>
                        <small>
                          {selectedInvoice.notes || "No additional notes provided."}
                        </small>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="float-end mt-3 mt-sm-0">
                        <p>
                          <b>Sub-total:</b>
                          <span className="float-end">
                            $
                            {selectedInvoice.items
                              .reduce((acc, item) => acc + Number(item.unitCost) * Number(item.quantity), 0)
                              .toFixed(2)}
                          </span>
                        </p>
                        <p>
                          <b>VAT ({selectedInvoice.vatRate}%):</b>
                          <span className="float-end">
                            $
                            {(
                              selectedInvoice.items.reduce(
                                (acc, item) => acc + Number(item.unitCost) * Number(item.quantity),
                                0
                              ) *
                              (selectedInvoice.vatRate / 100)
                            ).toFixed(2)}
                          </span>
                        </p>
                        <p>
                          <b>Additional Costs:</b>
                          <span className="float-end">${selectedInvoice.additionalCosts.toFixed(2)}</span>
                        </p>
                        <h3>
                          $
                          {(
                            selectedInvoice.items.reduce((acc, item) => acc + Number(item.unitCost) * Number(item.quantity),
                            0
                          ) +
                          selectedInvoice.items.reduce(
                            (acc, item) => acc + Number(item.unitCost) * Number(item.quantity),
                            0
                          ) *
                            (selectedInvoice.vatRate / 100) +
                          Number(selectedInvoice.additionalCosts)
                        ).toFixed(2)}{" "}
                        USD
                      </h3>
                    </div>
                  </Col>
                </Row>
                <div className="d-print-none mt-4">
                  <div className="d-flex flex-wrap gap-1 justify-content-center">
                    <Button
                      variant="primary"
                      onClick={() => {
                        window.print();
                      }}
                    >
                      <i className="ri-printer-line" /> Print
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          ) : (
            <Alert variant="info">Select an invoice to view details.</Alert>
          )}
        </Col>
      </Row>
    </div>
    <Footer />
  </React.Fragment>
);
};

export default Invoice;
